import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Printer, ShareNetwork, Lock, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { renderMarkdown } from "../lib/markdown";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditReport() {
  const { id } = useParams();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/audit/${id}`)
      .then((r) => setAudit(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleShare = async () => {
    if (!audit) return;
    setToggling(true);
    try {
      const { data } = await axios.patch(`${API}/audit/${id}/share`, { public: !audit.public });
      setAudit({ ...audit, public: data.public });
      toast.success(data.public ? "Audit is now public." : "Audit set to private.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update sharing");
    } finally {
      setToggling(false);
    }
  };

  const copyLink = () => {
    // Share a link to the server-rendered OG preview route so social platforms get rich previews.
    const API = process.env.REACT_APP_BACKEND_URL;
    const ogUrl = `${API}/api/audit/${id}/og`;
    navigator.clipboard.writeText(ogUrl);
    toast.success("Shareable link copied — rich previews enabled.");
  };

  if (loading) return <div className="container-wide py-16"><div className="h-4 w-48 bg-[#F5F5F5] animate-pulse" /></div>;

  if (err || !audit) {
    return (
      <div className="container-wide py-24 text-center">
        <p className="font-display text-3xl font-black">Audit not available</p>
        <p className="text-[#525252] mt-3">{err || "This report may be private or removed."}</p>
        <Link to="/" className="btn-primary mt-6 inline-flex">Back home</Link>
      </div>
    );
  }

  const content = audit.full || audit.partial || "";

  return (
    <div className="print-surface">
      <div className="container-wide py-8 md:py-10 print:hidden">
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.18em] text-[#525252] inline-flex items-center gap-2" data-testid="audit-back">
          <ArrowLeft size={14} weight="bold" /> Back
        </Link>
        <div className="mt-5 md:mt-6 flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Marketing Audit</p>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter mt-1 break-all">{audit.url}</h1>
            <p className="mt-2 text-[#737373] font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em]">
              Generated {new Date(audit.created_at).toLocaleString()} · {audit.public ? "Public" : "Private"}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => window.print()} className="btn-secondary" data-testid="audit-print-btn">
              <Printer size={16} weight="bold" /> Export PDF
            </button>
            <button onClick={copyLink} className="btn-secondary" data-testid="audit-copy-link-btn">
              <ShareNetwork size={16} weight="bold" /> Copy link
            </button>
            {audit.owned && user && (
              <button onClick={toggleShare} disabled={toggling} className={audit.public ? "btn-primary" : "btn-secondary"} data-testid="audit-share-toggle">
                {audit.public ? (
                  <>
                    <CheckCircle size={16} weight="bold" /> Public
                  </>
                ) : (
                  <>
                    <Lock size={16} weight="bold" /> Make public
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="container-wide pb-16 md:pb-24">
        <article
          className="prose-tight bg-white border border-[#E5E7EB] p-6 sm:p-8 md:p-12 max-w-3xl mx-auto"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
          data-testid="audit-report-content"
        />
        {!audit.full && (
          <div className="max-w-3xl mx-auto mt-6 border border-[#002FA7] bg-white p-6 print:hidden">
            <p className="font-display text-xl font-bold">This is the preview only.</p>
            <p className="text-[#525252] mt-2">Head to the homepage to unlock the full report with your email.</p>
            <Link to="/" className="btn-primary mt-4 inline-flex">Get full audit</Link>
          </div>
        )}
      </section>
    </div>
  );
}
